import { useEffect, useRef, useState } from "react";
import { FileText, Upload, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, openDocument } from "@/lib/api";
import { ago } from "@/lib/format";

export const DOC_TYPES = ["Contract Note", "Broker Screenshot", "Bank Transfer Screenshot", "Partnership Agreement", "Share Certificate", "Invoice", "Other"];

export function DocList({ docs = [] }) {
  if (!docs.length) return <div className="text-sm text-[#718096] py-3" data-testid="documents-empty">No documents yet</div>;
  return (
    <div>
      {docs.map((d) => (
        <button
          key={d.id}
          type="button"
          data-testid={`document-${d.id}`}
          onClick={() => openDocument(d.id).catch((e) => toast.error(errMsg(e)))}
          className="w-full flex items-center gap-3 py-3 border-b border-[#E2D9CF]/50 last:border-0 text-left hover:bg-[#FBF8F4] transition-colors"
        >
          <div className="w-10 h-10 rounded-xl bg-[#F9ECE8] text-[#C8553D] flex items-center justify-center shrink-0"><FileText size={18} /></div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{d.original_filename}</div>
            <div className="text-xs text-[#718096]">{d.doc_type} · {d.entity_type} · {d.uploaded_by_name} · {ago(d.created_at)}</div>
          </div>
          <ExternalLink size={15} className="text-[#A0AEC0]" />
        </button>
      ))}
    </div>
  );
}

export async function uploadDoc(entityType, entityId, file, docType = "Other") {
  const fd = new FormData();
  fd.append("entity_type", entityType);
  fd.append("entity_id", entityId);
  fd.append("doc_type", docType);
  fd.append("file", file);
  return api.post("/documents", fd);
}

export default function Documents({ entityType, entityId, initial, onUploaded }) {
  const [docs, setDocs] = useState(initial || []);
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [busy, setBusy] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (initial) { setDocs(initial); return; }
    api.get("/documents", { params: { entity_type: entityType, entity_id: entityId } }).then((r) => setDocs(r.data)).catch(() => {});
  }, [entityType, entityId, initial]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const { data } = await uploadDoc(entityType, entityId, f, docType);
      setDocs((d) => [data, ...d]);
      toast.success("Document uploaded");
      onUploaded && onUploaded();
    } catch (ex) {
      toast.error(errMsg(ex));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="hd-card" data-testid={`documents-panel-${entityType}`}>
      <div className="flex gap-2 mb-2">
        <select data-testid="document-type-select" className="hd-input h-11 flex-1 text-sm" value={docType} onChange={(e) => setDocType(e.target.value)}>
          {DOC_TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>
        <button type="button" data-testid="document-upload-button" className="hd-btn-ghost h-11 px-4" disabled={busy} onClick={() => ref.current?.click()}>
          <Upload size={16} /> {busy ? "Uploading…" : "Upload"}
        </button>
        <input ref={ref} type="file" className="hidden" data-testid="document-file-input" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.csv,.xlsx,.xls,.docx,.doc,.txt" onChange={onFile} />
      </div>
      <DocList docs={docs} />
    </div>
  );
}
